/**
 * Недельное расписание как ЛОКАЛЬНЫЙ ЧЕРНОВИК экрана: без React, без сети, чистые функции.
 *
 * Отдельный модуль по двум причинам сразу.
 *
 * Первая — `PUT /schedule` заменяет уровень ЦЕЛИКОМ (см. `replaceSchedule` в `api/cabinet.ts`).
 * Построчной правки не существует, потому что пересечение интервалов — свойство пары:
 * сохраняя строку за строкой, вторая легла бы поверх первой, и обе прошли бы проверку.
 * Значит, экран обязан держать всю неделю у себя и отправлять её одним запросом, а неделя
 * в виде «семь корзин по дням» удобнее плоского списка ровно настолько, насколько экран
 * рисуется по дням.
 *
 * Вторая — прогон тестов идёт в `environment: 'node'`, jsdom в проекте нет. Логика, которую
 * нельзя проверить без отрисовки, осталась бы непроверенной вовсе, поэтому всё, что можно
 * посчитать без DOM, посчитано здесь.
 *
 * Проверки повторяют серверные (`packages/backend/src/domain/schedule.ts`) НАМЕРЕННО и
 * никак их не заменяют: клиентская нужна, чтобы владелец не отправлял заведомо мусорный
 * набор, серверная — потому что клиенту верить нельзя. Ключи ошибок здесь те же самые
 * (`err.workingHoursInvalid`, `err.workingHoursOverlap`), чтобы отказ выглядел одинаково
 * независимо от того, кто его поймал.
 */

import type { WorkingIntervalDto } from '../../../api/cabinetTypes';

/** Сутки в минутах. Это допустимый `endMin`: 24:00 — конец дня, а не начало следующего. */
export const MINUTES_PER_DAY = 1440;

/** 0 = воскресенье … 6 = суббота, как в JS `Date.getDay()` и в колонке `working_hours.weekday`. */
export const WEEKDAY_COUNT = 7;

/** Интервал внутри дня. Дня в нём нет: его задаёт позиция в неделе. */
export interface DraftInterval {
  readonly startMin: number;
  readonly endMin: number;
}

/** Ровно семь корзин, индекс = `weekday`. Пустая корзина — выходной. */
export type WeekDraft = readonly (readonly DraftInterval[])[];

export type ScheduleProblemKey = 'err.workingHoursInvalid' | 'err.workingHoursOverlap';

export interface ScheduleProblem {
  readonly key: ScheduleProblemKey;
  readonly weekday: number;
}

export function emptyWeek(): WeekDraft {
  return Array.from({ length: WEEKDAY_COUNT }, () => []);
}

/**
 * Плоский набор с сервера → неделя.
 *
 * Неизвестный день недели не отбрасывается молча, а роняет разбор: тихо проглоченный
 * интервал выглядел бы как «сервер вернул выходной», и владелец сохранил бы неделю
 * без него, стерев рабочий день (AGENTS.md, правило 7).
 */
export function buildWeek(intervals: readonly WorkingIntervalDto[]): WeekDraft {
  const week: DraftInterval[][] = Array.from({ length: WEEKDAY_COUNT }, () => []);

  for (const interval of intervals) {
    const day = week[interval.weekday];
    if (!day) {
      throw new Error(`Расписание содержит недопустимый день недели: ${interval.weekday}`);
    }
    day.push({ startMin: interval.startMin, endMin: interval.endMin });
  }

  for (const day of week) day.sort(byStart);
  return week;
}

/** Неделя → плоский набор для `replaceSchedule`. Порядок нормализован: день, затем начало. */
export function flattenWeek(week: WeekDraft): WorkingIntervalDto[] {
  const flat: WorkingIntervalDto[] = [];
  week.forEach((day, weekday) => {
    for (const interval of [...day].sort(byStart)) {
      flat.push({ weekday, startMin: interval.startMin, endMin: interval.endMin });
    }
  });
  return flat;
}

/**
 * Совпадают ли два набора по существу.
 *
 * Нужна кнопке сохранения: без сравнения она либо всегда активна — и владелец шлёт PUT,
 * ничего не изменив, — либо активируется от любого касания поля. Порядок и представление
 * различием не считаются: сравниваются нормализованные наборы.
 */
export function sameSchedule(
  a: readonly WorkingIntervalDto[],
  b: readonly WorkingIntervalDto[],
): boolean {
  const key = (list: readonly WorkingIntervalDto[]): string =>
    flattenWeek(buildWeek(list))
      .map((interval) => `${interval.weekday}:${interval.startMin}-${interval.endMin}`)
      .join('|');
  return key(a) === key(b);
}

/** Замена одного дня. Неделя не мутируется: React обязан увидеть новый объект. */
export function withDay(
  week: WeekDraft,
  weekday: number,
  intervals: readonly DraftInterval[],
): WeekDraft {
  if (!Number.isInteger(weekday) || weekday < 0 || weekday >= WEEKDAY_COUNT) {
    throw new Error(`Недопустимый день недели: ${weekday}`);
  }
  return week.map((day, index) => (index === weekday ? [...intervals].sort(byStart) : day));
}

/**
 * Проверка одного дня. Повторяет `assertValidInterval` и проверку пересечений бэкенда.
 *
 * Касание границами (конец одного равен началу соседа) пересечением НЕ считается — так же,
 * как на сервере: смежные интервалы склеивает генератор слотов (ТЗ §19, решение 14),
 * и запрет заставлял бы владельца сливать строки руками.
 */
export function dayProblem(day: readonly DraftInterval[], weekday: number): ScheduleProblem | null {
  for (const interval of day) {
    const { startMin, endMin } = interval;
    const sane =
      Number.isInteger(startMin) &&
      Number.isInteger(endMin) &&
      startMin >= 0 &&
      endMin <= MINUTES_PER_DAY &&
      // Строго больше: смена через полночь моделью рабочих часов не выражается вовсе —
      // у строки нет даты, и «02:00» относилось бы к тем же суткам.
      endMin > startMin;
    if (!sane) return { key: 'err.workingHoursInvalid', weekday };
  }

  const sorted = [...day].sort(byStart);
  for (let index = 1; index < sorted.length; index += 1) {
    const previous = sorted[index - 1];
    const current = sorted[index];
    if (!previous || !current) continue;
    if (previous.endMin > current.startMin) return { key: 'err.workingHoursOverlap', weekday };
  }

  return null;
}

/** Первая проблема недели или `null`. Первая, а не список: кнопка сохранения одна. */
export function weekProblem(week: WeekDraft): ScheduleProblem | null {
  for (let weekday = 0; weekday < week.length; weekday += 1) {
    const problem = dayProblem(week[weekday] ?? [], weekday);
    if (problem) return problem;
  }
  return null;
}

const HH_MM = /^(\d{1,2}):(\d{2})$/;

/**
 * «ЧЧ:ММ» → минуты от полуночи. `null` — строка не является временем.
 *
 * `null`, а не ноль: ноль означал бы полночь, и опечатка в поле превращалась бы
 * в законный интервал с 00:00 вместо видимой ошибки ввода.
 *
 * `24:00` допускается — это верхняя граница `endMin` на бэкенде, то есть салон,
 * закрывающийся ровно в полночь. `24:30` — уже нет.
 */
export function parseHhMm(text: string): number | null {
  const match = HH_MM.exec(text.trim());
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours > 24 || minutes > 59) return null;

  const total = hours * 60 + minutes;
  return total > MINUTES_PER_DAY ? null : total;
}

/** Минуты от полуночи → «ЧЧ:ММ». Всегда 24 часа: 12-часового формата нет ни в RU, ни в UZ. */
export function formatHhMm(minutes: number): string {
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > MINUTES_PER_DAY) {
    throw new Error(`Недопустимое время в минутах от полуночи: ${minutes}`);
  }
  const hours = Math.floor(minutes / 60);
  return `${pad2(hours)}:${pad2(minutes % 60)}`;
}

/** «10:00–14:00, 15:00–20:00» — строка дня в списке недели. */
export function formatDaySummary(day: readonly DraftInterval[]): string {
  return [...day]
    .sort(byStart)
    .map((interval) => `${formatHhMm(interval.startMin)}–${formatHhMm(interval.endMin)}`)
    .join(', ');
}

function byStart(a: DraftInterval, b: DraftInterval): number {
  return a.startMin - b.startMin;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}
